import * as React from "react";
import { useCallback } from "react";
import { Download } from "lucide-react";
import type { Exporter, RaRecord } from "ra-core";
import {
  defaultExporter,
  fetchRelatedRecords,
  useDataProvider,
  useGetResourceLabel,
  useNotify,
  useListContext,
  useResourceTranslation,
} from "ra-core";
import { Button } from "../ui/button";

/**
 * `jsonexport` (the default exporter's CSV library) flattens an array field
 * by splitting it across one CSV row per array element — fine for a plain
 * array of strings/numbers (`tags: ["a", "b"]` becomes `a;b` in one cell),
 * but for an array of objects (e.g. an order's `items`, a schedule's `days`)
 * it instead emits one ragged row per element, leaving every other column
 * on that row blank (ticket feedback: "csv... has a lot of empty lines").
 * Stringify only the object-array case up front so jsonexport treats it as
 * one opaque cell instead of flattening it.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

function stringifyObjectArrays(value: unknown): unknown {
  if (Array.isArray(value)) {
    const hasObjectElement = value.some(
      (element) => element !== null && typeof element === "object" && !(element instanceof Date),
    );
    return hasObjectElement ? JSON.stringify(value) : value;
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        key,
        stringifyObjectArrays(nested),
      ]),
    );
  }
  return value;
}

/**
 * A button that exports the currently displayed page of list data to a file.
 *
 * Respects current filters and sort order. Exports only the records already
 * loaded for the current cursor-paginated page — it never issues a separate
 * fetch-everything request (pagination here has no total count, so
 * "export everything matching the filter" isn't a well-defined page size;
 * see `ResourceTable`'s `SelectAllButton` ban for the same reasoning).
 * Use a custom exporter to customize the result and fetch related records.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/exportbutton/ ExportButton documentation}
 *
 * @example
 * import { CreateButton, ExportButton, TopToolbar } from '.';
 *
 * const PostListActions = () => (
 *   <>
 *     <FilterButton />
 *     <CreateButton />
 *     <ExportButton />
 *   </>
 * );
 *
 * export const PostList = () => (
 *   <List actions={<PostListActions />}>
 *     ...
 *   </List>
 * );
 */
export const ExportButton = (props: ExportButtonProps) => {
  const {
    onClick,
    label: labelProp,
    icon = defaultIcon,
    exporter: customExporter,
    className = "cursor-pointer",
  } = props;
  const { data, total, resource, exporter: exporterFromContext } =
    useListContext();
  const getResourceLabel = useGetResourceLabel();
  const label = useResourceTranslation({
    resourceI18nKey: `resources.${resource}.action.export`,
    baseI18nKey: "ra.action.export",
    options: {
      name: getResourceLabel(resource, 1),
    },
    userText: labelProp,
  });
  const exporter =
    customExporter ||
    (exporterFromContext === undefined ? defaultExporter : exporterFromContext);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!data || !exporter) {
        return;
      }

      const exportData =
        exporter === defaultExporter
          ? (data.map(stringifyObjectArrays) as RaRecord[])
          : data;

      Promise.resolve(
        exporter(exportData, fetchRelatedRecords(dataProvider), dataProvider, resource),
      ).catch((error) => {
        console.error(error);
        notify("HTTP Error", { type: "error" });
      });
      if (typeof onClick === "function") {
        onClick(event);
      }
    },
    [data, dataProvider, exporter, notify, onClick, resource],
  );

  return (
    <Button
      variant="outline"
      onClick={handleClick}
      disabled={total === 0}
      className={className}
    >
      {icon}
      {label}
    </Button>
  );
};

const defaultIcon = <Download />;

export interface ExportButtonProps {
  className?: string;
  exporter?: Exporter;
  icon?: React.ReactNode;
  label?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  resource?: string;
}
